import { formatPhoneNumberIntl, isValidPhoneNumber } from 'react-phone-number-input';

/**
 * Format a phone number for display
 *
 * Handles both E.164 format (+14155551234) and legacy raw US digits (4155551234)
 *
 * @param {string} value - Phone number in E.164 or raw digits format
 * @returns {string} Formatted phone number for display
 */
export const formatPhoneDisplay = value => {
  if (!value) return '';

  // If it's already E.164 format, use the library formatter
  if (value.startsWith('+')) {
    return formatPhoneNumberIntl(value) || value;
  }

  // Handle legacy raw digits format (assume US)
  const digits = value.replace(/\D/g, '');

  // 10 digits - US number without country code
  if (digits.length === 10) {
    const formatted = formatPhoneNumberIntl(`+1${digits}`);
    // Return national format for US numbers (without +1)
    return formatted
      ? formatted.replace('+1 ', '')
      : `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // 11 digits starting with 1 - US number with country code
  if (digits.length === 11 && digits.startsWith('1')) {
    const formatted = formatPhoneNumberIntl(`+${digits}`);
    return formatted
      ? formatted.replace('+1 ', '')
      : `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  // Fallback: basic US formatting for partial numbers
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

/**
 * Normalize a phone number to E.164 format
 *
 * @param {string} value - Phone number in any format
 * @param {string} defaultCountry - Default country code (default: 'US')
 * @returns {string} Phone number in E.164 format or original value if can't normalize
 */
const normalizePhoneToE164 = (value, defaultCountry = 'US') => {
  if (!value) return '';

  // Already E.164
  if (value.startsWith('+')) {
    return value;
  }

  const digits = value.replace(/\D/g, '');

  // 10 digits - assume US
  if (digits.length === 10) {
    return `+1${digits}`;
  }

  // 11 digits starting with 1 - assume US with country code
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }

  return value;
};

/**
 * Validate a phone number
 *
 * @param {string} value - Phone number to validate
 * @returns {boolean} True if valid phone number
 */
export const validatePhoneNumber = value => {
  if (!value) return true; // Empty is valid (use required for mandatory)

  const normalized = normalizePhoneToE164(value);
  return isValidPhoneNumber(normalized);
};
